/* FieldRender — renders a single CMS field by type.
 * Uses React.createElement for the recursive (rows) call to avoid an
 * infinite-loop bug in the visual-edits babel plugin when a component
 * references itself in JSX. */
import React from "react";
import {
  TextField,
  NumberField,
  TextArea,
  SelectField,
  SwitchField,
  ImagePickerField,
  RepeatableRows,
  LinkMap,
} from "./_fields";

function TagsField({ label, value, onChange, help }) {
  const arr = Array.isArray(value)
    ? value
    : value
    ? String(value).split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  return (
    <TextField
      label={label}
      value={arr.join(", ")}
      onChange={(v) =>
        onChange(v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [])
      }
      help={help || "Comma-separated values"}
    />
  );
}

function RowBlock({ field, row, update }) {
  const rowFields = field.rowFields || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rowFields.map((rf) => {
        const wide =
          rf.type === "longtext" || rf.type === "textarea" || rf.type === "image";
        return (
          <div key={rf.key} className={wide ? "md:col-span-2" : ""}>
            {React.createElement(FieldRender, {
              field: rf,
              value: row?.[rf.key],
              onChange: (v) => update({ [rf.key]: v }),
            })}
          </div>
        );
      })}
    </div>
  );
}

export default function FieldRender({ field, value, onChange, errors }) {
  const err = errors?.[field.key];
  const testId = `field-${field.key}`;
  const t = field.type;
  if (t === "text" || t === "slug" || t === "email" || t === "url") {
    return (
      <TextField
        label={field.label}
        value={value}
        onChange={onChange}
        required={field.required}
        help={field.help}
        error={err}
        testId={testId}
      />
    );
  }
  if (t === "number") {
    return (
      <NumberField
        label={field.label}
        value={value}
        onChange={onChange}
        required={field.required}
        help={field.help}
        error={err}
        testId={testId}
      />
    );
  }
  if (t === "textarea") {
    return (
      <TextArea
        label={field.label}
        value={value}
        onChange={onChange}
        rows={field.rows || 4}
        required={field.required}
        help={field.help}
        error={err}
        testId={testId}
      />
    );
  }
  if (t === "longtext") {
    return (
      <TextArea
        label={field.label}
        value={value}
        onChange={onChange}
        rows={field.rows || 10}
        required={field.required}
        help={field.help}
        error={err}
        testId={testId}
      />
    );
  }
  if (t === "select") {
    return (
      <SelectField
        label={field.label}
        value={value}
        onChange={onChange}
        options={field.options || []}
        required={field.required}
        help={field.help}
        error={err}
        testId={testId}
      />
    );
  }
  if (t === "switch") {
    return (
      <SwitchField
        label={field.label}
        value={!!value}
        onChange={onChange}
        help={field.help}
        testId={testId}
      />
    );
  }
  if (t === "image") {
    return (
      <ImagePickerField
        label={field.label}
        value={value}
        onChange={onChange}
        help={field.help}
        category={field.category || "other"}
        testId={testId}
      />
    );
  }
  if (t === "linkmap") {
    return (
      <LinkMap
        label={field.label}
        value={value || {}}
        onChange={onChange}
        keys={field.keys || []}
        help={field.help}
        testId={testId}
      />
    );
  }
  if (t === "tags") {
    return <TagsField label={field.label} value={value} onChange={onChange} help={field.help} />;
  }
  if (t === "rows") {
    return (
      <RepeatableRows
        label={field.label}
        items={value || []}
        onChange={onChange}
        defaultItem={field.defaultItem || {}}
        help={field.help}
        addLabel={field.addLabel || "Add"}
        testId={testId}
        renderRow={(row, update) => <RowBlock field={field} row={row} update={update} />}
      />
    );
  }
  if (t === "stringRows") {
    /* Array of plain strings — show one text field per row. */
    const items = Array.isArray(value) ? value : [];
    return (
      <RepeatableRows
        label={field.label}
        items={items}
        onChange={onChange}
        defaultItem=""
        help={field.help}
        addLabel={field.addLabel || "Add"}
        testId={testId}
        renderRow={(row, _update, i) => (
          <TextField
            label={field.itemLabel || `Item ${i + 1}`}
            value={row || ""}
            onChange={(v) => {
              const next = items.slice();
              next[i] = v;
              onChange(next);
            }}
          />
        )}
      />
    );
  }
  return <div className="text-xs text-red-600">Unsupported field type: {t}</div>;
}
